import { useAuthenticator } from '@aws-amplify/ui-react';
import { Button, Spinner } from '@nrg/design-system';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cognitoErrorAuthKey } from '@/auth/cognito-error-message';
import AuthFooterLink from '@/auth/components/AuthFooterLink';
import AuthScreen from '@/auth/components/AuthScreen';
import PasswordField from '@/auth/components/PasswordField';
import PasswordRules, { evaluatePassword } from '@/auth/components/PasswordRules';

export default function ForceNewPasswordScreen() {
  const { t } = useTranslation('auth');
  const { isPending, error, submitForm, signOut } = useAuthenticator((c) => [
    c.isPending,
    c.error,
    c.route,
  ]);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const { allPassed: pwOk } = evaluatePassword(password, t);
  const passwordError = submitAttempted && !pwOk ? t('errors.password_policy_failed') : undefined;
  const confirmError =
    submitAttempted && password !== confirmPassword ? t('errors.passwords_mismatch') : undefined;

  const cognitoKey = cognitoErrorAuthKey(error);
  const banner = cognitoKey ? t(cognitoKey) : null;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitAttempted(true);
    if (!pwOk || password !== confirmPassword) return;
    submitForm({ password, confirm_password: confirmPassword });
  };

  return (
    <AuthScreen
      heading={t('force_new_password.heading')}
      subhead={t('force_new_password.subhead')}
      error={banner}
      onSubmit={handleSubmit}
      footer={<AuthFooterLink onClick={() => signOut()}>{t('common.sign_out')}</AuthFooterLink>}
    >
      <PasswordField
        name="new-password"
        label={t('force_new_password.new_password')}
        autoComplete="new-password"
        autoFocus
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        error={passwordError}
        disabled={isPending}
      />
      <PasswordRules password={password} />
      <PasswordField
        name="confirm-password"
        label={t('force_new_password.confirm_password')}
        autoComplete="new-password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        error={confirmError}
        disabled={isPending}
      />
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isPending}
        iconLeft={isPending ? <Spinner size="sm" /> : undefined}
      >
        {t('force_new_password.submit')}
      </Button>
    </AuthScreen>
  );
}
